class SodiiStoryCarousel extends HTMLElement {
  #track;
  #cards = [];
  #previousButton;
  #nextButton;
  #activeIndex = 0;
  #frame;
  #dragStartX = 0;
  #dragStartScrollLeft = 0;
  #dragged = false;
  #videoObserver;

  connectedCallback() {
    this.#track = this.querySelector('[data-story-track]');
    if (!this.#track) return;

    this.#cards = [...this.querySelectorAll('[data-story-card]')];
    this.#previousButton = this.querySelector('[data-story-previous]');
    this.#nextButton = this.querySelector('[data-story-next]');

    this.#track.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#track.addEventListener('keydown', this.#handleKeydown);
    this.#track.addEventListener('pointerdown', this.#handlePointerDown);
    this.#track.addEventListener('pointermove', this.#handlePointerMove);
    this.#track.addEventListener('pointerup', this.#finishDrag);
    this.#track.addEventListener('pointercancel', this.#finishDrag);
    this.#track.addEventListener('click', this.#preventDraggedClick, true);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    window.addEventListener('resize', this.#handleScroll, { passive: true });

    this.#observeVideos();
    this.#updateCarousel();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#videoObserver?.disconnect();
    this.#track?.removeEventListener('scroll', this.#handleScroll);
    this.#track?.removeEventListener('keydown', this.#handleKeydown);
    this.#track?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#track?.removeEventListener('pointermove', this.#handlePointerMove);
    this.#track?.removeEventListener('pointerup', this.#finishDrag);
    this.#track?.removeEventListener('pointercancel', this.#finishDrag);
    this.#track?.removeEventListener('click', this.#preventDraggedClick, true);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    window.removeEventListener('resize', this.#handleScroll);
  }

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#updateCarousel);
  };

  #updateCarousel = () => {
    if (!this.#track) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      this.classList.remove('is-expanded');
    } else {
      this.classList.toggle('is-expanded', this.#track.scrollLeft > 12);
    }

    if (!this.#cards.length) return;

    const trackBounds = this.#track.getBoundingClientRect();
    const padding = Number.parseFloat(getComputedStyle(this.#track).paddingLeft) || 0;
    const startEdge = trackBounds.left + padding;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.#cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - startEdge);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        this.#activeIndex = index;
      }
    });

    this.#cards.forEach((card, index) => card.toggleAttribute('aria-current', index === this.#activeIndex));

    const maxScroll = Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
    const atStart = this.#track.scrollLeft <= 2 || this.#activeIndex === 0;
    const atEnd = this.#track.scrollLeft >= maxScroll - 2 || this.#activeIndex === this.#cards.length - 1;

    if (this.#previousButton) this.#previousButton.disabled = atStart;
    if (this.#nextButton) this.#nextButton.disabled = atEnd;
  };

  #scrollToCard(index) {
    const nextIndex = Math.max(0, Math.min(index, this.#cards.length - 1));
    const card = this.#cards[nextIndex];
    if (!card) return;

    const trackBounds = this.#track.getBoundingClientRect();
    const cardBounds = card.getBoundingClientRect();
    const padding = Number.parseFloat(getComputedStyle(this.#track).paddingLeft) || 0;
    const left = this.#track.scrollLeft + cardBounds.left - trackBounds.left - padding;

    this.#track.scrollTo({
      left,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #handleKeydown = (event) => {
    let nextIndex;

    if (event.key === 'ArrowLeft') nextIndex = this.#activeIndex - 1;
    if (event.key === 'ArrowRight') nextIndex = this.#activeIndex + 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.#cards.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.#scrollToCard(nextIndex);
  };

  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('a, button, input, select, textarea')) return;

    event.preventDefault();
    this.#dragStartX = event.clientX;
    this.#dragStartScrollLeft = this.#track.scrollLeft;
    this.#dragged = false;
    this.#track.classList.add('is-dragging');
    this.#track.setPointerCapture(event.pointerId);
  };

  #handlePointerMove = (event) => {
    if (!this.#track.classList.contains('is-dragging')) return;

    const distance = event.clientX - this.#dragStartX;
    if (Math.abs(distance) > 4) this.#dragged = true;
    this.#track.scrollLeft = this.#dragStartScrollLeft - distance;
  };

  #finishDrag = (event) => {
    if (!this.#track.classList.contains('is-dragging')) return;

    this.#track.classList.remove('is-dragging');
    if (this.#track.hasPointerCapture(event.pointerId)) this.#track.releasePointerCapture(event.pointerId);

    this.#updateCarousel();
    this.#scrollToCard(this.#activeIndex);
  };

  #preventDraggedClick = (event) => {
    if (!this.#dragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.#dragged = false;
  };

  #handleClick = (event) => {
    if (event.target.closest('[data-story-previous]')) {
      this.#scrollToCard(this.#activeIndex - 1);
      return;
    }

    if (event.target.closest('[data-story-next]')) {
      this.#scrollToCard(this.#activeIndex + 1);
      return;
    }

    this.#handleSoundToggle(event);
  };

  #handleSoundToggle = (event) => {
    const button = event.target.closest('[data-sound-toggle]');
    if (!button || !this.contains(button)) return;

    const card = button.closest('[data-story-card]');
    const video = card?.querySelector('video');
    if (!video) return;

    const shouldUnmute = video.muted;
    if (shouldUnmute) {
      this.querySelectorAll('[data-story-card] video').forEach((otherVideo) => {
        if (otherVideo === video) return;
        otherVideo.muted = true;
        this.#syncSoundButton(otherVideo);
      });
    }

    video.muted = !shouldUnmute;
    video.play().catch(() => {});
    this.#syncSoundButton(video);
  };

  #syncSoundButton(video) {
    const button = video.closest('[data-story-card]')?.querySelector('[data-sound-toggle]');
    if (!button) return;

    const isSoundOn = !video.muted;
    const title = video.closest('[data-story-card]')?.querySelector('.sodii-story-carousel__card-title')?.textContent?.trim();
    button.setAttribute('aria-pressed', isSoundOn.toString());
    button.setAttribute('aria-label', `${isSoundOn ? 'Turn sound off' : 'Turn sound on'} for ${title || 'this video'}`);

    const label = button.querySelector('[data-sound-label]');
    if (label) label.textContent = isSoundOn ? 'Sound on' : 'Sound off';
  }

  #observeVideos() {
    const videos = this.querySelectorAll('[data-story-card] video');
    if (!videos.length || !('IntersectionObserver' in window)) return;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
            video.play().catch(() => {});
            return;
          }

          video.pause();
          video.muted = true;
          this.#syncSoundButton(video);
        });
      },
      { root: this.#track, threshold: [0, 0.45] }
    );

    videos.forEach((video) => this.#videoObserver.observe(video));
  }

  #handleBlockSelect = (event) => {
    const card = event.target.closest?.('[data-story-card]');
    if (!card || !this.contains(card)) return;

    card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };
}

if (!customElements.get('sodii-story-carousel')) {
  customElements.define('sodii-story-carousel', SodiiStoryCarousel);
}
